//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useEffect, useContext } from 'react';
import { FormControl, FormControlLabel, makeStyles, Radio, RadioGroup } from '@material-ui/core';
import ReCAPTCHA from 'react-google-recaptcha';
import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../../system/firebase';
import useCountryStateCity from '../../common/hooks/useCountryStateCity';
import { HOMEPAGE_CONTACT, PAGE_TITLE_CONTACT_US, PAGE_TITLE_CONTACT_US_SEO, RECAPTCHA_SITE_KEY } from '../../common/constants';
import { LayoutContext, AppContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { Grid } from '@material-ui/core';
import AfitdTextField from '../../common/elemental/AfitdTextField';
import AfitdCountryAutocomplete from '../../common/elemental/AfitdCountryAutocomplete';
import Footer from './Footer';
import AfitdButton from '../../common/elemental/AfitdButton';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Input, { isValidPhoneNumber, isPossiblePhoneNumber, formatPhoneNumberIntl } from 'react-phone-number-input/input';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { logPageView } from '../../system/firebase';
import { REQUIRED } from '../../common/constants';
import {
    PLACEHOLDER_FIELD_EMAIL,
    PLACEHOLDER_FIELD_FIRST_NAME,
    PLACEHOLDER_FIELD_LAST_NAME,
    PLACEHOLDER_FIELD_PHONE,
    PLACEHOLDER_FIELD_JOB_TITLE,
} from '../../common/placeholderHelper';
import { submitContactUsForm } from '../../common/services/customerSupportServices';
import { validateEmail } from '../../common/emailHelper';
import { AFITD_DOMAIN, PATH_HOME } from '../../system/constants';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdPageHead from '../../common/elemental/AfitdPageHead';
import AfitdTypography from '../../common/elemental/AfitdTypography';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
// import SubComponent1Example from './SubComponent1Example';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _forEach from 'lodash/forEach';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles(theme => ({
    root: {
        margin: 0,
        padding: 0,
        background: 'white',
        display: 'flex',
        flexDirection: 'column',
    },
    contactsUsMainContainer: { flex: 'auto' },
    contactsUsContainer: { textAlign: 'center', paddingBottom: 52 },
    radioContainer: { marginLeft: '25px' },
    inputWidth: { maxWidth: '100%', margin: '10px 0px 30px 0px', boxSizing: 'border-box' },
    submitButtonContainer: { padding: '32px 0px 148px 0px ' },
    submitButton: { textTransform: 'capitalize', width: '100%', padding: 17, fontSize: 18, fontWeight: 500 },
    inputPaddingLeft: { paddingLeft: '10px', [theme.breakpoints.down('sm')]: { paddingLeft: '0px', marginTop: 20 } },
    inputPaddingRight: { paddingRight: '10px', [theme.breakpoints.down('sm')]: { paddingRight: '0px' } },
    contactsFormSection: {},
    formInputContainer: {
        marginBottom: '20px',
    },
    formInputItem: {
        label: {
            color: 'red',
        },
    },
    messageBox: { maxWidth: '100%', padding: '10px 0px', boxSizing: 'border-box' },
    checkBoxContainer: {},
    inputWidthLast: { maxWidth: '100%', margin: '10px 0px 30px 0px', boxSizing: 'border-box' },
    fieldLevel: { fontWeight: 'bold', marginBottom: '100px !important' },
    suggestionSpan: { [theme.breakpoints.down('xs')]: { paddingLeft: '42px' } },
    reCaptchaContainer: { marginTop: '10px' },
    reCaptchaError: { color: '#f44336 !important', marginTop: '5px', fontSize: '0.75rem', marginLeft: '15px' },
    firstName: { marginTop: '10px' },
    lastName: { marginTop: '10px' },
    email: { marginTop: '10px' },
    phone: { marginTop: '10px' },
    company: { marginTop: '10px' },
    jobTitle: { marginTop: '10px' },
    phoneInput: {
        fontSize: '16px',
        '&:focus': {
            borderColor: `${theme.palette.secondary.main} !important`,
        },
        borderWidth: '2px',
        outline: 'none !important',
        padding: '17px',
        borderRadius: '4px',
        border: '2px solid #c4c4c4',
        display: 'block',
        width: '100%',
        marginTop: '9px',
        '&::placeholder': {
            color: '#a2a2a2',
        },
    },
    phoneInputError: {
        fontSize: '16px',
        '&:focus': {
            borderColor: `#f44336 !important`,
        },
        borderWidth: '2px',
        outline: 'none !important',
        padding: '17px',
        borderRadius: '4px',
        border: '2px solid #f44336',
        display: 'block',
        width: '100%',
        marginTop: '9px',
        '&::placeholder': {
            color: '#a2a2a2',
        },
    },
    countryPadding: {
        paddingLeft: '12px',
        [theme.breakpoints.down('sm')]: { paddingLeft: '0px', marginTop: 20 },
    },
    phoneErrorText: {
        color: '#f44336',
        fontSize: '12px',
        marginLeft: '14px',
        marginTop: '3px',
    },
    thankYouContainer: {
        flex: 'auto',
        minHeight: '600px',
        padding: '30px',
        justifyContent: 'center',
    },
    thankYouText: {
        textAlign: 'center',
    },
    checkCirlce: {
        color: theme.palette.primary.main,
        fontSize: '100px',
    },
    thankYouheading: {
        fontSize: '30px',
        margin: 0,
    },
    finishButton: {
        width: '250px',
    },

    errorMessage: { color: '#f44336 !important', marginTop: '5px', fontSize: '1rem', marginLeft: '15px' },
    state: {
        marginTop: '10px',
    },
    country: {
        marginTop: '10px',
    },
}));

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
var isDeveMode = false;
window.location.hostname === 'localhost' ? (isDeveMode = true) : (isDeveMode = false);

const getDefns = params => {
    const {
        classes,
        formik,
        history,
        haveSuggestion,
        setHaveSuggestion,
        needToReport,
        setNeedToReport,
        showRadio,
        setShowRadio,
        isGeneralRecommedation,
        setIsGeernalRecommendation,
        isFromMedia,
        setIsFromMedia,
        handleChangeCountry,
        handleChangeState,
        countryLov,
        stateLov,
    } = params;

    const defns = {
        contactsUsContainer: {},
        radioContainer: {},
        inputWidth: {},
        submitButton: {},
        inputPaddingLeft: {},
        inputPaddingRight: {},
        contactsFormSection: { container: true, direction: 'row', justify: 'center' },
        contactsFormGrid: { item: true, xl: 7, lg: 11, md: 10, sm: 11, xs: 11 },
        formInputContainer: { container: true },
        thankYouContainer: { container: true },
        formInputItem: { item: true, xl: 6, lg: 6, md: 6, sm: 12, xs: 12 },
        messageBox: {
            ...formik.getFieldProps('message'),
            // placeholder: PLACEHOLDER_FIELD_FIRST_NAME,
            id: 'message',
            error: formik.touched?.message && !!formik.errors?.message,
            helperText: formik.errors?.message,
            onChange: formik.handleChange,
        },
        checkBoxContainer: {},
        firstName: {
            ...formik.getFieldProps('firstName'),
            placeholder: PLACEHOLDER_FIELD_FIRST_NAME,
            id: 'firstName',
            error: formik.touched?.firstName && !!formik.errors?.firstName,
            helperText: formik.errors?.firstName,
            onChange: formik.handleChange,
        },
        lastName: {
            placeholder: PLACEHOLDER_FIELD_LAST_NAME,
            id: 'lastName',
            ...formik.getFieldProps('lastName'),
            error: formik.touched?.lastName && !!formik.errors?.lastName,
            helperText: formik.errors?.lastName,
        },

        email: {
            placeholder: PLACEHOLDER_FIELD_EMAIL,
            id: 'email',
            ...formik.getFieldProps('email'),
            error: formik.touched?.email && !!formik.errors?.email,
            helperText: formik.errors?.email,
        },
        phone: {
            placeholder: 'Ex. +1 (301) 567-9544',
            id: 'phone',
            ...formik.getFieldProps('phone'),
        },
        company: {
            placeholder: PLACEHOLDER_FIELD_PHONE,
            id: 'company',
            ...formik.getFieldProps('company'),
            error: formik.touched?.company && !!formik.errors?.company,
            helperText: formik.errors?.company,
        },
        country: {
            onChange: handleChangeCountry,
            value: formik.values.country,
            options: countryLov,
            id: 'country',
            placeholder: '+ Ex. United State',
            error: formik.touched?.country && Boolean(formik.errors?.country),
            helperText: formik.errors?.country,
        },
        state: {
            onChange: handleChangeState,
            value: formik.values.state,
            options: stateLov,
            id: 'state',
            placeholder: '+ Ex. California',
            error: formik.touched?.state && Boolean(formik.errors?.state),
            helperText: formik.errors?.state,
        },
        jobTitle: {
            placeholder: PLACEHOLDER_FIELD_JOB_TITLE,
            id: 'jobTitle',
            ...formik.getFieldProps('jobTitle'),
        },
        suggestion: {
            checked: haveSuggestion,
            onChange: e => setHaveSuggestion(e.target.checked),
        },
        generalRecommendation: {
            checked: isGeneralRecommedation,
            onChange: e => setIsGeernalRecommendation(e.target.checked),
        },
        isFromMedia: {
            checked: isFromMedia,
            onChange: e => setIsFromMedia(e.target.checked),
        },
        needToReport: {
            checked: needToReport,
            onChange: e => {
                setNeedToReport(e.target.checked);
                setShowRadio(!showRadio);
            },
        },
        finishButton: {
            color: 'secondary',
            fullwidth: true,
            onClick: () => history.push('/'),
        },
    };

    // Automatically inject className for matching keys
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const validate = values => {
    const errors = {};
    const fieldNames = ['email', 'firstName', 'lastName', 'selectedOption', 'country', 'state', 'message'];
    if (!isDeveMode) fieldNames.push('reCaptcha');

    // required fields for login and sign up
    _forEach(fieldNames, fieldName => {
        if (!_get(values, fieldName)) {
            _set(errors, fieldName, REQUIRED);
        }
        if (_get(values, 'email')) {
            const result = validateEmail(_get(values, 'email'));
            if (result === null) _set(errors, 'email', 'Please enter a valid email');
        }
    });

    return errors;
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const ContactUs = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles();
    const history = useHistory();
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.down('xs'));

    const { setSnackbarMessage, setSnackbarErrorMessage } = useContext(LayoutContext);

    const { userId, lovs, isEmp } = useContext(AppContext);

    const { scrollToPageTop } = useContext(LayoutContext);

    const { countryLov, stateLov, setCountry, setState, setCity, setCityLocation } = useCountryStateCity({
        defaultCountryLov: _get(lovs, 'countryLov'),
    });

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    // const { propExample } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [recaptcha, setReCaptcha] = React.useState(null);
    const [haveSuggestion, setHaveSuggestion] = useState(false);
    const [needToReport, setNeedToReport] = useState(false);
    const [isGeneralRecommedation, setIsGeernalRecommendation] = useState(false);
    const [isFromMedia, setIsFromMedia] = useState(false);
    const [value, setValue] = useState('');
    const [selectedOption, setSelectedOption] = useState('');
    const [showRadio, setShowRadio] = useState(false);
    const [phone, setPhone] = useState('');
    const [phoneError, setPhoneError] = useState(false);
    const [formSubmitted, setformSubmitted] = useState(false);
    const [userTypeError, setUserTypeError] = useState(true);

    //---------------------------------------------------------------------
    // Formik
    //---------------------------------------------------------------------
    const formik = useFormik({
        initialValues: {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            company: '',
            jobTitle: '',
            reCaptcha: '',
            message: '',
            country: '',
            state: '',
        },
        validate: validate.bind(null),
        validateOnBlur: false,
        validateOnChange: true,
        onSubmit: async values => {
            let validated = true;
            let data = {
                firstName: _get(values, 'firstName'),
                lastName: _get(values, 'lastName'),
                email: _get(values, 'email'),
                company: _get(values, 'company'),
                jobTitle: _get(values, 'jobTitle'),
                country: _get(values, 'country'),
                message: _get(values, 'message'),
                state: _get(values, 'state'),
                phone: formatPhoneNumberIntl(phone),
                softwareSuggestion: selectedOption === 'softwareSuggestion' ? true : null,
                general: selectedOption === 'general' ? true : null,
                media: selectedOption === 'media' ? true : null,
                other: selectedOption === 'other' ? true : null,
            };
            if (selectedOption === 'reportBug') {
                data = { ...data, reportBug: true, userType: value };
            }

            if (userTypeError) validated = false;

            if (validated) await submitContactUsForm({ data, userId, setSnackbarMessage, setSnackbarErrorMessage, setformSubmitted });
        },
    });

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        logPageView(PAGE_TITLE_CONTACT_US);
    }, []);

    // on mount, scroll to the top of the page
    useEffect(() => {
        scrollToPageTop();
    }, [scrollToPageTop, formSubmitted]);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const onReCaptchaChange = value => {
        setReCaptcha(value);
        formik.setFieldValue('reCaptcha', value, true);
    };

    const handleChange = event => {
        setValue(event.target.value);
        setUserTypeError(false);
    };

    const handleChangeSelectedOption = event => {
        const val = event.target.value;
        setSelectedOption(val);
        formik.setFieldValue('selectedOption', val);
        if (val === 'reportBug') {
            setShowRadio(true);
            setUserTypeError(true);
        } else {
            setShowRadio(false);
            setUserTypeError(false);
            setValue('');
        }
    };

    const handleChangeCountry = (evt, option) => {
        formik.setFieldValue('country', option?.name, true);
        setCountry(option?.name);
        setState(null);
        setCity(null);
        setCityLocation(null);
    };
    const handleChangeState = (evt, option) => {
        formik.setFieldValue('state', option?.name, true);
        setState(option?.name);
        setCity(null);
        setCityLocation(null);
    };

    const onChangePhone = val => {
        if (val) {
            setPhone(val);
            if (isPossiblePhoneNumber(val) && isValidPhoneNumber(val)) setPhoneError(false);
            else setPhoneError(true);
        } else {
            setPhoneError(false);
        }
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        classes,
        props,
        history,
        formik,
        haveSuggestion,
        setHaveSuggestion,
        needToReport,
        setNeedToReport,
        showRadio,
        setShowRadio,
        isGeneralRecommedation,
        setIsGeernalRecommendation,
        isFromMedia,
        setIsFromMedia,
        handleChangeCountry,
        handleChangeState,
        countryLov,
        stateLov,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div {...defns.root}>
            <AfitdPageHead
                title={PAGE_TITLE_CONTACT_US_SEO}
                description="Let us help you take the next step in your job search and achieve your professional goals. Get in touch with us!"
                keywords="hire talented employees, 60-Second Interview Pitch, Interview Pitch"
                canonicalUrl={`https://${AFITD_DOMAIN}${PATH_HOME}/${HOMEPAGE_CONTACT}`}
            />
            {formSubmitted ? (
                <Grid {...defns.thankYouContainer}>
                    <div {...defns.thankYouText}>
                        <CheckCircleIcon {...defns.checkCirlce} />
                        <p {...defns.thankYouheading}>Thank You</p>
                        <p>We sincerely appreciate you reaching out to us</p>
                        <p>If an inquiry was made and we owe you a response, please standby.</p>
                        <br />
                        <br />
                        <AfitdButton {...defns.finishButton}>Finished</AfitdButton>
                    </div>
                </Grid>
            ) : (
                <Grid {...defns.contactsUsMainContainer}>
                    <Grid {...defns.contactsUsContainer}>
                        <h1>Contact Us</h1>
                    </Grid>

                    {/* contact us form */}
                    <Grid {...defns.contactsFormSection}>
                        <Grid {...defns.contactsFormGrid}>
                            <form onSubmit={formik.handleSubmit}>
                                <Grid {...defns.formInputContainer}>
                                    <Grid {...defns.formInputItem} {...defns.inputPaddingRight}>
                                        <label {...defns.fieldLevel} htmlFor="firstName">
                                            First Name: *
                                        </label>
                                        <AfitdTextField {...defns.firstName} />
                                    </Grid>

                                    <Grid {...defns.formInputItem} {...defns.inputPaddingLeft}>
                                        <label {...defns.fieldLevel} htmlFor="lastName">
                                            Last Name: *
                                        </label>
                                        <AfitdTextField id="lastName" {...defns.lastName} placeholder="Ex. Sparrow" />
                                    </Grid>
                                </Grid>

                                <Grid {...defns.formInputContainer}>
                                    <Grid {...defns.formInputItem} {...defns.inputPaddingRight}>
                                        <label {...defns.fieldLevel} htmlFor="email">
                                            Email: *
                                        </label>
                                        <AfitdTextField id="email" {...defns.email} placeholder="Ex. jack@sparrow.com" />
                                    </Grid>
                                    <Grid {...defns.formInputItem} {...defns.inputPaddingLeft}>
                                        <label {...defns.fieldLevel} htmlFor="jobTitle">
                                            Job title:
                                        </label>
                                        <AfitdTextField id="jobTitle" {...defns.jobTitle} placeholder="Ex. Designer" />
                                    </Grid>
                                </Grid>

                                <Grid {...defns.formInputContainer}>
                                    <Grid {...defns.formInputItem} {...defns.inputPaddingRight}>
                                        <label {...defns.fieldLevel} htmlFor="country">
                                            Select your Country: *
                                        </label>
                                        <AfitdCountryAutocomplete {...defns.country} />
                                    </Grid>
                                    <Grid {...defns.formInputItem} {...defns.inputPaddingLeft}>
                                        <label {...defns.fieldLevel} htmlFor="state">
                                            Select your State/Province: *
                                        </label>
                                        <AfitdCountryAutocomplete {...defns.state} />
                                    </Grid>
                                </Grid>

                                <Grid {...defns.formInputContainer}>
                                    <Grid {...defns.formInputItem} style={{ paddingRight: isEmp ? '10px' : '45px' }}>
                                        <label {...defns.fieldLevel} htmlFor="phone">
                                            Phone:
                                        </label>

                                        <Input
                                            country="US"
                                            international
                                            withCountryCallingCode
                                            className={phoneError ? classes.phoneInputError : classes.phoneInput}
                                            value={phone}
                                            onChange={val => onChangePhone(val)}
                                            placeholder="Ex. +1 (301) 567-9544"
                                        />
                                        {phoneError && <AfitdTypography className={classes.phoneErrorText}>Phone number is not valid</AfitdTypography>}
                                    </Grid>
                                    <Grid {...defns.formInputItem} {...defns.countryPadding}>
                                        <label {...defns.fieldLevel} htmlFor="company">
                                            Company:
                                        </label>
                                        <AfitdTextField id="company" {...defns.company} placeholder="Ex. Dream Company" />
                                    </Grid>
                                </Grid>

                                <Grid {...defns.checkBoxContainer}>
                                    <FormControl component="fieldset">
                                        <RadioGroup aria-label="report" name="report" value={selectedOption} onChange={handleChangeSelectedOption}>
                                            <FormControlLabel value="softwareSuggestion" control={<Radio />} label="I have a software improvement suggestion" />
                                            <FormControlLabel value="reportBug" control={<Radio />} label="I need to report a glitch/bug" />
                                            {showRadio ? (
                                                <>
                                                    <FormControl component="fieldset">
                                                        <RadioGroup aria-label="report" name="report" value={value} onChange={handleChange}>
                                                            <div {...defns.radioContainer}>
                                                                <FormControlLabel value="EMP" control={<Radio />} label="I am an Employer" />
                                                                {matches && <br />}
                                                                <FormControlLabel value="JS" control={<Radio />} label="I am a Job Seeker" />
                                                            </div>
                                                        </RadioGroup>
                                                        {(value === '' || userTypeError) && <AfitdTypography {...defns.errorMessage}>Please select one option</AfitdTypography>}
                                                    </FormControl>
                                                    <br />
                                                </>
                                            ) : null}
                                            <FormControlLabel value="general" control={<Radio />} label="General recommendation" />
                                            <FormControlLabel value="media" control={<Radio />} label="I’m from the media" />
                                            <FormControlLabel value="other" control={<Radio />} label="Other" />
                                        </RadioGroup>
                                        {formik.errors.selectedOption && <AfitdTypography {...defns.errorMessage}>Please select one of the options above</AfitdTypography>}
                                    </FormControl>
                                    <br />
                                    {/* <AfitdCheckbox id="suggestion" {...defns.suggestion} />
                                    <label htmlFor="suggestion">
                                        I have a software improvement <span {...defns.suggestionSpan}>suggestion.</span>{' '}
                                    </label>{' '}
                                    <br />
                                    <AfitdCheckbox id="report" {...defns.needToReport} />
                                    <label htmlFor="report">I need to report a glitch/bug.</label>
                                    <br />
                                    {showRadio ? (
                                        <>
                                            <FormControl component="fieldset">
                                                <RadioGroup aria-label="report" name="report" value={value} onChange={handleChange}>
                                                    <div {...defns.radioContainer}>
                                                        <FormControlLabel
                                                            value="EMP"
                                                            control={<Radio />}
                                                            label="I am an Employer"
                                                            style={{ color: isBlack ? 'black' : 'rgba(17, 30, 20, 0.4)' }}
                                                            onClick={() => setIsBlack(true)}
                                                        />
                                                        {matches && <br />}
                                                        <FormControlLabel
                                                            value="JS"
                                                            control={<Radio />}
                                                            label="I am a Job Seeker"
                                                            style={{ color: isBlack ? 'rgba(17, 30, 20, 0.4)' : ' black' }}
                                                            onClick={() => setIsBlack(false)}
                                                        />
                                                    </div>
                                                </RadioGroup>
                                            </FormControl>
                                            <br />
                                        </>
                                    ) : null}
                                    <AfitdCheckbox id="recommendation" {...defns.generalRecommendation} />
                                    <label htmlFor="recommendation">General recommendation</label>
                                    <br />
                                    <AfitdCheckbox id="media" {...defns.isFromMedia} />
                                    <label htmlFor="media">I’m from the media</label> */}
                                </Grid>

                                <br />
                                <br />
                                <label {...defns.fieldLevel} htmlFor="message">
                                    Message:
                                </label>
                                <AfitdTextField {...defns.messageBox} id="message" multiline={true} rows={8} placeholder="Your message..." />
                                {!isDeveMode && (
                                    <div {...defns.reCaptchaContainer}>
                                        <Grid {...defns.reCaptchaContainer}>
                                            <ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} onChange={onReCaptchaChange} />
                                        </Grid>
                                        {!!formik.errors?.reCaptcha && recaptcha === '' && <AfitdTypography {...defns.reCaptchaError}>Please Verify yourself</AfitdTypography>}
                                    </div>
                                )}
                                <br />
                                <br />

                                <div {...defns.submitButtonContainer}>
                                    <AfitdButton variant="contained" color="secondary" type="submit" value="Submit" {...defns.submitButton}>
                                        Submit
                                    </AfitdButton>
                                </div>
                            </form>
                        </Grid>
                    </Grid>
                </Grid>
            )}
            <Footer />
        </div>
    );
};

export default ContactUs;
